import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { BffError } from '../bff/errors'
import { useT } from '../i18n'
import { Button, EmptyState } from '../ui'

interface Props {
  readonly children: ReactNode
  /** Distinguishes the boundary in logs. A single app-wide boundary means any
   *  render error blanks the whole product; screens get their own. */
  readonly area: string
}
interface State {
  readonly error: Error | null
}

/**
 * React error boundaries must be class components — there is still no hook
 * equivalent for componentDidCatch.
 *
 * The fallback is deliberately not a bare "something went wrong": when the
 * cause is a BffError it shows the request id, which is the only string that
 * links what the user saw to the BFF and API logs. Without it a support
 * conversation starts from nothing.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.area}]`, error, info.componentStack)
  }

  private readonly reset = (): void => this.setState({ error: null })

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    return <ErrorFallback error={this.state.error} onRetry={this.reset} />
  }
}

function ErrorFallback({ error, onRetry }: { readonly error: Error; readonly onRetry: () => void }) {
  const t = useT()
  const requestId = error instanceof BffError ? error.requestId : null
  return (
    <EmptyState
      glyph="⚠"
      title={t('error.title')}
      body={requestId ? `${t('error.body')} ${t('error.reference', { requestId })}` : t('error.body')}
      actions={<Button onClick={onRetry}>{t('error.retry')}</Button>}
    />
  )
}
